import { BufferImageSource, Texture } from 'pixi.js'
import { skyness } from '../../lib/nightGrade'

/**
 * The per photo masks, computed once and handed to the shader as a texture.
 *
 * WHY A TEXTURE AND NOT SHADER CODE: openness is a sequential scan down each
 * column - every pixel's value depends on the one above it - and a fragment
 * shader has no way to express that. So it runs once when the photo loads and
 * the result is uploaded. That also means dragging the night slider never
 * touches the CPU.
 *
 * The channel layout is the seam Phase Two plugs into. Adding a building,
 * vegetation, ground or window mask later means writing more channels here and
 * reading them in the shader; the night renderer does not need redesigning,
 * and nothing else in the app needs to know how a mask was produced - by this
 * arithmetic today, by a segmentation model later.
 *
 *   R  sky          how much this pixel is sky
 *   G  openness     how much open sky this surface can see
 *   B  reserved     (building / vegetation)
 *   A  reserved     (window / ground)
 */

const MASK_MAX = 256

export interface NightMask {
  texture: Texture
  width: number
  height: number
}

export function buildNightMask(source: HTMLImageElement | HTMLCanvasElement): NightMask {
  const sw = 'naturalWidth' in source ? source.naturalWidth : source.width
  const sh = 'naturalHeight' in source ? source.naturalHeight : source.height
  const scale = Math.min(1, MASK_MAX / Math.max(1, sw))
  const w = Math.max(1, Math.round(sw * scale))
  const h = Math.max(1, Math.round(sh * scale))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(source, 0, 0, w, h)
  const { data } = ctx.getImageData(0, 0, w, h)

  // --- sky ---------------------------------------------------------------
  const sky = new Float32Array(w * h)
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    sky[p] = skyness(data[i], data[i + 1], data[i + 2], Math.floor(p / w) / h)
  }

  // --- openness ----------------------------------------------------------
  //
  // Accumulated straight down each column from whatever sky is above it, with
  // a decay, so a roof plane just under open sky scores high and a wall under
  // a deep porch scores nothing. This is what takes a metal roof dark while
  // the siding beneath it holds its tone.
  const open = new Float32Array(w * h)
  const decay = Math.pow(0.5, 1 / Math.max(4, h * 0.18))
  const acc = new Float32Array(w)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const v = sky[y * w + x]
      acc[x] = Math.max(acc[x] * decay, v)
      open[y * w + x] = acc[x]
    }
  }

  // Blurred, hard. Without this the per column accumulation paints a vertical
  // gradient down a flat wall - the gray stain down the white gable that got
  // circled in red on an earlier build.
  const blurred = boxBlur(open, w, h, Math.max(3, Math.round(h * 0.07)))

  // A driveway has the house above it in image space and so scores nothing,
  // but it is looking straight up at the whole sky. Ground gets it back.
  const out = new Uint8Array(w * h * 4)
  for (let p = 0; p < w * h; p++) {
    const yNorm = Math.floor(p / w) / Math.max(1, h - 1)
    const ground = smoothstep(0.55, 0.85, yNorm) * 0.85
    const notSky = 1 - sky[p]
    const o = Math.min(1, Math.max(blurred[p] * notSky, ground * notSky))
    out[p * 4] = Math.round(Math.min(1, sky[p]) * 255)
    out[p * 4 + 1] = Math.round(o * 255)
    out[p * 4 + 2] = 0
    out[p * 4 + 3] = 255
  }

  const texture = new Texture({
    source: new BufferImageSource({
      resource: out,
      width: w,
      height: h,
      format: 'rgba8unorm',
      // Smooth, because the mask is a quarter of the photo's resolution and a
      // nearest-neighbor lookup would draw visible steps across a roof.
      scaleMode: 'linear',
    }),
  })

  return { texture, width: w, height: h }
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)))
  return t * t * (3 - 2 * t)
}

/** Separable box blur over a Float32 plane. */
function boxBlur(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(w * h)
  const out = new Float32Array(w * h)
  const k = 2 * r + 1

  for (let y = 0; y < h; y++) {
    let sum = 0
    for (let x = -r; x <= r; x++) sum += src[y * w + clampi(x, 0, w - 1)]
    for (let x = 0; x < w; x++) {
      tmp[y * w + x] = sum / k
      sum -= src[y * w + clampi(x - r, 0, w - 1)]
      sum += src[y * w + clampi(x + r + 1, 0, w - 1)]
    }
  }
  for (let x = 0; x < w; x++) {
    let sum = 0
    for (let y = -r; y <= r; y++) sum += tmp[clampi(y, 0, h - 1) * w + x]
    for (let y = 0; y < h; y++) {
      out[y * w + x] = sum / k
      sum -= tmp[clampi(y - r, 0, h - 1) * w + x]
      sum += tmp[clampi(y + r + 1, 0, h - 1) * w + x]
    }
  }
  return out
}

const clampi = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v)
